/* import __COLOCATED_TEMPLATE__ from './standard-modal-dialog.hbs.js'; */
import { debounce } from '@ember/runloop';
import { reads } from '@ember/object/computed';
import Component from '@ember/component';
import { isActionableElement } from '@square/glass-ui/services/focus';

/**

## Overview

Dialogs are UI windows that prompt the user for input or action. All modal
dialogs must use this component to wrap other modal elements; namely, the header,
footer, and content.

You rarely need to use this component directly. Instead, use `{{deprecated/standard-modal-layout}}`.

@param {Boolean} [hasDividers=true] - If true, shows lines above and below the content. Note that dividers always appear if the inner content scrolls
@param {Function} onEnterKeyDown - Function to run when hitting the ENTER key
@param {String} autofocusSelector - If provided, autofocuses the actionable element found by the selector in the standard dialog. Otherwise the first actionable element is focused.

*/

const KEY_ENTER = 13;

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['dialog--modal'],
  classNameBindings: ['isShowingDividers:dialog--modal--with-dividers'],
  ariaRole: 'dialog',
  title: null,
  helpText: null,
  primaryButtonConfig: null,
  secondaryButtonConfig: null,
  tertiaryButtonConfig: null,
  hasDividers: true,
  isShowingSpinner: false,
  autofocusSelector: null,
  // internal use only: reads from the public API but can be changed
  isShowingDividers: reads('hasDividers'),
  didInsertElement(...args) {
    this._super(...args);

    // add a resize event handler to force dividers if the modal content scrolls
    if (!this.hasDividers) {
      this._windowEventListener = () => debounce(this, this._forceDividersWhenContentScrolls, 50);
      window.addEventListener('resize', this._windowEventListener);
    }
  },
  didRender(...args) {
    this._super(...args);
    if (!this.hasDividers) {
      this._forceDividersWhenContentScrolls();
    }
  },
  didDestroyElement(...args) {
    this._super(...args);
    window.removeEventListener('resize', this._windowEventListener);
  },
  keyDown(e) {
    if (e.keyCode === KEY_ENTER && !isActionableElement(e.target) && this.onEnterKeyDown) {
      this.onEnterKeyDown(e);
    }
  },
  _forceDividersWhenContentScrolls() {
    if (this.isDestroyed) {
      return;
    }
    const contentElement = this.element.querySelector('.dialog--modal__content');
    if (!contentElement) {
      return;
    }
    const height = contentElement.offsetHeight;
    const scrollHeight = contentElement.scrollHeight;

    // something about the fact that padding changes makes for
    // weird rounding issues. Allowing a difference of 1 removes
    // some visual glitching.
    if (scrollHeight - height > 1) {
      this.set('isShowingDividers', true);
    } else if (!this.hasDividers) {
      this.set('isShowingDividers', false);
    }
  }
});